import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { QRCodeSVG } from 'qrcode.react';

import ImageScroller from '../../components/imagescroller';
import localUtil from '../../lib/util';

function documentImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    return img.s3LargeLink || img.s3MediumLink;
}

@observer
class DocumentView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showFullScreen: false,
        };
    }

    loadAll = async (props = this.props) => {
        const { id } = props;
        const { documentStore } = this.props.stores;
        if (id) {
            documentStore.updateKeyValue('document', {});
            await documentStore.load(id, false, {});
        }
    }

    editDocument = e => {
        e.preventDefault();
        e.stopPropagation();
        const { appState, documentStore } = this.props.stores;
        const { document = {} } = documentStore;
        const { drawerLevel = 1, callback } = this.props;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('editDocument', {
            height: drawerHeightMediumLarge,
            id: document.id,
            callback: () => {
                this.loadAll(this.props);
                if (callback) callback();
            },
        }, drawerLevel + 1);
    }

    toggleFullScreen = () => {
        this.setState({ showFullScreen: !this.state.showFullScreen });
    }

    getExpiryStatus = (expiryDate) => {
        if (!expiryDate) return null;
        const expiry = new Date(expiryDate);
        const now = new Date();
        const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

        if (expiry < now) {
            return { status: 'expired', class: 'danger', text: <Text id='document.expired'>Expired</Text> };
        }
        if (expiry <= thirtyDaysFromNow) {
            return { status: 'expiring', class: 'warning', text: <Text id='document.expiring-soon'>Expiring soon</Text> };
        }
        return { status: 'valid', class: 'success', text: <Text id='document.valid'>Valid</Text> };
    }

    getDocumentTypeIcon = (documentType) => {
        const typeIcons = {
            'driving-license': 'fa-duotone fa-car',
            'fishing-license': 'fa-duotone fa-fish',
            'hunting-license': 'fa-duotone fa-deer',
            'boat-license': 'fa-duotone fa-ship',
            'weapons-license': 'fa-duotone fa-gun',
            'hunting-permit': 'fa-duotone fa-deer',
            'weapons-permit': 'fa-duotone fa-gun',
            'travel-permit': 'fa-duotone fa-plane',
            'event-ticket': 'fa-duotone fa-ticket',
            'travel-ticket': 'fa-duotone fa-plane-departure',
            'membership-card': 'fa-duotone fa-id-card',
            'certificate': 'fa-duotone fa-certificate',
            'id-card': 'fa-duotone fa-id-card',
            'passport': 'fa-duotone fa-passport',
            'other': 'fa-duotone fa-file',
        };
        return typeIcons[documentType] || 'fa-duotone fa-file';
    }

    getDocumentTypeName = (documentType) => {
        const typeNames = {
            'driving-license': 'Driving License',
            'fishing-license': 'Fishing License',
            'hunting-license': 'Hunting License',
            'boat-license': 'Boat License',
            'weapons-license': 'Weapons License',
            'hunting-permit': 'Hunting Permit',
            'weapons-permit': 'Weapons Permit',
            'travel-permit': 'Travel Permit',
            'event-ticket': 'Event Ticket',
            'travel-ticket': 'Travel Ticket',
            'membership-card': 'Membership Card',
            'certificate': 'Certificate',
            'id-card': 'ID Card',
            'passport': 'Passport',
            'other': 'Other',
        };
        return typeNames[documentType] || documentType || 'Unknown';
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const { editFunction } = this.props;
        const { showFullScreen } = this.state;
        const { appState, userStore, documentStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { document = {} } = documentStore;

        const expiryStatus = this.getExpiryStatus(document.expiryDate);
        const icon = this.getDocumentTypeIcon(document.documentType);
        const typeName = this.getDocumentTypeName(document.documentType);
        const hasImages = document.images?.length > 0;
        const hasBarcode = document.ticketBarcode;
        const isTicket = document.documentType === 'event-ticket' || document.documentType === 'travel-ticket';

        // Full screen view for images/QR
        if (showFullScreen) {
            return (
                <div
                    class='position-fixed d-flex flex-column align-items-center justify-content-center'
                    style='top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.95); z-index: 100000;'
                    onClick={this.toggleFullScreen}
                >
                    <div class='text-white mb-3' style='font-size: 1.5rem;'>
                        {document.title}
                    </div>
                    {hasImages ? (
                        <img
                            src={document.images[0].s3LargeLink || document.images[0].s3MediumLink}
                            style='max-width: 95vw; max-height: 80vh; object-fit: contain;'
                            alt={document.title}
                        />
                    ) : hasBarcode ? (
                        <div class='bg-white p-4 rounded'>
                            <QRCodeSVG value={document.ticketBarcode} size={300} />
                        </div>
                    ) : (
                        <div class='text-white'>
                            <i class={`${icon}`} style='font-size: 200px;' />
                        </div>
                    )}
                    <div class='text-white mt-3'>
                        <small><Text id='document.tap-to-close'>Tap to close</Text></small>
                    </div>
                </div>
            );
        }

        return (
            <div class='w-100 d-flex justify-content-center position-relative'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 0px; padding-bottom: 100px;'>
                    {/* Edit button */}
                    {(editFunction || true) && (
                        <div class='position-absolute' style='top: 20px; right: 65px; z-index: 100000;'>
                            <button onClick={this.editDocument} class='btn btn-sm btn-outline-secondary rounded-pill'>
                                <i class='fa-regular fa-pen me-2' />
                                <Text id='document.edit'>Edit</Text>
                            </button>
                        </div>
                    )}

                    <div class='w-100 d-flex flex-column'>
                        {/* Main image/QR display - PROMINENT */}
                        <div
                            class='d-flex flex-column align-items-center justify-content-center p-3'
                            style='min-height: 300px; cursor: pointer;'
                            onClick={this.toggleFullScreen}
                        >
                            {hasImages ? (
                                <div class='w-100'>
                                    <ImageScroller
                                        images={document.images || []}
                                        stores={this.props.stores}
                                        showImg={img => documentImg(img, this.props, '1920x')}
                                        maxHeight='50vh'
                                        rounded={true}
                                    />
                                </div>
                            ) : hasBarcode ? (
                                <div class={`p-4 rounded-lg ${darkmode ? 'bg-white' : 'bg-white'}`}>
                                    <QRCodeSVG value={document.ticketBarcode} size={250} />
                                    <div class='text-center mt-2 text-dark'>
                                        <small>{document.ticketBarcode}</small>
                                    </div>
                                </div>
                            ) : (
                                <div class={`d-flex flex-column align-items-center justify-content-center p-5 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-light'}`}>
                                    <i class={`${icon} text-primary mb-3`} style='font-size: 120px;' />
                                    <span class='text-muted'><Text id='document.no-image'>No image uploaded</Text></span>
                                </div>
                            )}
                            <small class='text-muted mt-2'>
                                <i class='fa-solid fa-expand me-1' />
                                <Text id='document.tap-fullscreen'>Tap for fullscreen</Text>
                            </small>
                        </div>

                        {/* Document info */}
                        <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                            {/* Title and type */}
                            <div class='d-flex flex-row align-items-center mb-2'>
                                <i class={`${icon} me-3`} style='font-size: 2rem;' />
                                <div class='flex-fill'>
                                    <h4 class='mb-0'>{document.title}</h4>
                                    <span class='text-muted'>{typeName}</span>
                                </div>
                                {expiryStatus && (
                                    <span class={`badge bg-${expiryStatus.class}`}>
                                        {expiryStatus.text}
                                    </span>
                                )}
                            </div>

                            {/* Document number */}
                            {document.documentNumber && (
                                <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                    <i class='fa-solid fa-hashtag text-muted me-3' style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.number'>Document Number</Text></small>
                                        <strong>{document.documentNumber}</strong>
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            {document.description && (
                                <div class='d-flex flex-row align-items-start py-2 border-bottom'>
                                    <i class='fa-solid fa-align-left text-muted me-3 mt-1' style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.description'>Description</Text></small>
                                        <span>{document.description}</span>
                                    </div>
                                </div>
                            )}

                            {/* Issue date */}
                            {document.issueDate && (
                                <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                    <i class='fa-solid fa-calendar-plus text-muted me-3' style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.issued'>Issued</Text></small>
                                        <span>{util.formatDate(document.issueDate, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                    </div>
                                </div>
                            )}

                            {/* Expiry date */}
                            {document.expiryDate && (
                                <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                    <i class={`fa-solid fa-calendar-xmark me-3 ${expiryStatus?.status === 'expired' ? 'text-danger' : expiryStatus?.status === 'expiring' ? 'text-warning' : 'text-muted'}`} style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.expires'>Expires</Text></small>
                                        <span class={expiryStatus?.status === 'expired' ? 'text-danger' : ''}>
                                            {util.formatDate(document.expiryDate, { day: 'numeric', month: 'long', year: 'numeric' })}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Issued by */}
                            {document.issuedBy && (
                                <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                    <i class='fa-solid fa-building text-muted me-3' style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.issuedBy'>Issued By</Text></small>
                                        <span>{document.issuedBy}</span>
                                    </div>
                                </div>
                            )}

                            {/* Issued country */}
                            {document.issuedCountry && (
                                <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                    <i class='fa-solid fa-flag text-muted me-3' style='width: 20px;' />
                                    <div>
                                        <small class='text-muted d-block'><Text id='document.issuedCountry'>Country</Text></small>
                                        <span>{document.issuedCountry}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Weapon license specific info */}
                        {(document.documentType === 'weapons-license' || document.documentType === 'weapons-permit') && (document.weaponType || document.weaponCategory || document.weaponRestrictions) && (
                            <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <h6 class='mb-3'>
                                    <i class='fa-solid fa-gun me-2' />
                                    <Text id='document.weapon-details'>Weapon License Details</Text>
                                </h6>
                                {document.weaponType && (
                                    <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.weaponType'>Weapon Type</Text></small>
                                            <span>{document.weaponType}</span>
                                        </div>
                                    </div>
                                )}
                                {document.weaponCategory && (
                                    <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.weaponCategory'>Category</Text></small>
                                            <span>{document.weaponCategory}</span>
                                        </div>
                                    </div>
                                )}
                                {document.weaponRestrictions && (
                                    <div class='d-flex flex-row align-items-start py-2'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.weaponRestrictions'>Restrictions</Text></small>
                                            <span>{document.weaponRestrictions}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Ticket/event specific info */}
                        {isTicket && (document.eventName || document.eventDate || document.eventVenue || document.eventSeat) && (
                            <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <h6 class='mb-3'>
                                    <i class='fa-solid fa-ticket me-2' />
                                    <Text id='document.event-details'>Event Details</Text>
                                </h6>
                                {document.eventName && (
                                    <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.eventName'>Event</Text></small>
                                            <strong style='font-size: 1.2rem;'>{document.eventName}</strong>
                                        </div>
                                    </div>
                                )}
                                {document.eventDate && (
                                    <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.eventDate'>Date & Time</Text></small>
                                            <span>{util.formatDate(document.eventDate, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    </div>
                                )}
                                {document.eventVenue && (
                                    <div class='d-flex flex-row align-items-center py-2 border-bottom'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.eventVenue'>Venue</Text></small>
                                            <span>{document.eventVenue}</span>
                                        </div>
                                    </div>
                                )}
                                {document.eventSeat && (
                                    <div class='d-flex flex-row align-items-center py-2'>
                                        <div>
                                            <small class='text-muted d-block'><Text id='document.eventSeat'>Seat</Text></small>
                                            <strong>{document.eventSeat}</strong>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Notes */}
                        {document.notes && (
                            <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <h6 class='mb-2'>
                                    <i class='fa-solid fa-sticky-note me-2' />
                                    <Text id='document.notes'>Notes</Text>
                                </h6>
                                <p class='mb-0' style='white-space: pre-wrap;'>{document.notes}</p>
                            </div>
                        )}

                        {/* Tags */}
                        {document.tags?.length > 0 && (
                            <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <div class='d-flex flex-wrap'>
                                    {document.tags.map(tag => (
                                        <span class={`badge ${darkmode ? 'text-bg-dark' : 'text-bg-light'} me-1 mb-1`}>
                                            #{tag.name || tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Files */}
                        {document.files?.length > 0 && (
                            <div class={`d-flex flex-column mx-3 mt-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <h6 class='mb-2'>
                                    <i class='fa-solid fa-paperclip me-2' />
                                    <Text id='document.files'>Attached Files</Text>
                                </h6>
                                <div class='d-flex flex-column'>
                                    {document.files.map(file => (
                                        <a
                                            href={file.s3Link}
                                            class='badge text-bg-light border me-2 mb-1'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <i class='fa-solid fa-file me-1' />
                                            {file.name}
                                            <span class='font-weight-lighter ms-1'>
                                                ({localUtil.formatFileSize(file.size)})
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default DocumentView;
